import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export interface ContentBlock {
  type: "paragraph" | "heading" | "list" | "video";
  text: string;
  items?: string[];
  videoId?: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  meta_title: string | null;
  meta_description: string | null;
  category: string | null;
  read_time: string | null;
  author: string | null;
  published_date: string | null;
  image_url: string | null;
  featured_video: string | null;
  content: ContentBlock[] | null;
  faqs: FAQItem[] | null;
  local_seo: string | null;
  related_articles: string[] | null;
}

// Database row type (with Json types)
interface ArticleRow {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  meta_title: string | null;
  meta_description: string | null;
  category: string | null;
  read_time: string | null;
  author: string | null;
  published_date: string | null;
  image_url: string | null;
  featured_video: string | null;
  content: Json | null;
  faqs: Json | null;
  local_seo: string | null;
  related_articles: string[] | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

// Transform database row to Article type
const transformArticle = (row: ArticleRow): Article => ({
  id: row.id,
  slug: row.slug,
  title: row.title,
  excerpt: row.excerpt,
  meta_title: row.meta_title,
  meta_description: row.meta_description,
  category: row.category,
  read_time: row.read_time,
  author: row.author,
  published_date: row.published_date,
  image_url: row.image_url,
  featured_video: row.featured_video,
  content: Array.isArray(row.content) ? row.content as unknown as ContentBlock[] : null,
  faqs: Array.isArray(row.faqs) ? row.faqs as unknown as FAQItem[] : null,
  local_seo: row.local_seo,
  related_articles: row.related_articles,
});

// Load all published articles (for listing page)
export const useArticles = () => {
  return useQuery({
    queryKey: ['articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('id, slug, title, excerpt, category, read_time, author, published_date, image_url')
        .eq('is_published', true)
        .order('published_date', { ascending: false });
      
      if (error) throw error;
      return (data || []).map(row => ({
        ...row,
        content: null,
        faqs: null,
        local_seo: null,
        related_articles: null,
        meta_title: null,
        meta_description: null,
        featured_video: null,
      })) as Article[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Load a single article by slug
export const useArticle = (slug: string) => {
  return useQuery({
    queryKey: ['article', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();
      
      if (error) throw error;
      if (!data) throw new Error('Article non trouvé');
      return transformArticle(data as ArticleRow);
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!slug,
  });
};

// URL canonique pour copier/partager directement (agence8020.com)
export const getArticleCanonicalUrl = (slug: string): string => {
  return `https://agence8020.com/articles/${slug}`;
};

// URL pour les réseaux sociaux (Edge Function avec métadonnées OG)
export const getArticleShareUrl = (slug: string): string => {
  return `https://sytjhiezemqcukdrrcja.supabase.co/functions/v1/article-og/articles/${slug}`;
};

// Get social share links
export const getShareLinks = (slug: string, title: string) => {
  const shareUrl = getArticleShareUrl(slug);
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
  };
};
